import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import { cloneDeep, size } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import {
  GetLeagueFormatTemplate,
  PutLeagueFormatTemplate,
  TPutLeagueFormatTemplate,
  DeleteLeagueFormatTemplate,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/formatTemplate";
import { GetLeagueDivision } from "api/rpc/2024-04/facilityAdmin/league/scoring/division";
import { GetSeasonLeaderboard } from "api/rpc/2024-04/facilityAdmin/league/scoring/leaderboard";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import {
  TCompetitionFormat,
  TCompetitionFormatHoles,
  TCompetitionHandicap,
  TCompetitionOrganization,
  TCompetitionTeamScores,
  TLeagueDivision,
  TPlayerCompetition,
  TLeagueSeasonLeaderboard,
} from "redux/reducers/models/league";
import { handleChangeEventInput } from "helpers/Helpers";

import useLeagueScoringFormatOptions from "../../scoring/rounds/scoringFormat/useLeagueScoringFormatOptions";
import useModal from "hooks/modals/useModal";
import Page from "components/page/Page";
import Card from "components/card/Card";
import { Select } from "components/select";
import FormLayout from "components/form/FormLayout";
import { IPrimaryPageAction } from "components/page/PageActions";
import Input from "components/form/input";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew/";
import DataTable from "components/dataTable/DataTable";
import Callout from "components/callout/Callout";

import "../../scoring/rounds/scoringFormat/leagueScoringFormatNew.scss";
import Popup from "components/popup/Popup";

export default function LeagueFormatTemplate() {
  const history = useHistory();
  const { t } = useTranslation();
  const { leagueId, templateId } = useParams<{
    leagueId: string;
    templateId: string;
  }>();

  const dispatch = useAppDispatch();

  const {
    organizationOptions,
    formatOptions,
    formatHoleOptions,
    handicapOptions,
    teamScoreOptions,
    playerCompetitionOptions,
  } = useLeagueScoringFormatOptions();

  const [templateState, setTemplateState] = useState<TPutLeagueFormatTemplate>({
    format_template_id: templateId,
    name: "",
    league_division_id: undefined,
    title: "",
    organization: undefined,
    format: undefined,
    competition: undefined,
    default_participation_points: 0,
    holes: undefined,
    handicap: undefined,
    handicap_allowance: 100,
    team_scores_best_gross: 0,
    team_scores_best_net: 0,
    custom_points: undefined,
    leaderboard_data: [],
  });
  const [leaderboards, setLeaderboards] = useState<TLeagueSeasonLeaderboard[]>([]);
  const [filteredLeaderboards, setFilteredLeaderboards] = useState<TLeagueSeasonLeaderboard[]>([]);
  const [divisions, setDivisions] = useState<TLeagueDivision[]>([]);

  const {
    state: customPointsModal,
    updateModal: updateCustomPointsModal,
    closeModal: closeCustomPointsModal,
  } = useModal({
    selectedCustomPointsIndex: null,
    customPoints: null,
  });

  const { state: deletePopup, updateModal: updateDeletePopup, closeModal: closeDeletePopup } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTemplate(source.token);
    void loadLeagueLeaderboards(leagueId, source.token);
    void loadLeagueDivision(leagueId, source.token);
    return () => source.cancel();
  }, [leagueId]);

  useEffect(() => {
    const filter = leaderboards
      ?.filter(leaderboard => leaderboard.league_division_id === templateState.league_division_id)
      ?.filter(leaderboard => leaderboard.organization === templateState.organization);
    setFilteredLeaderboards(filter);
  }, [templateState.organization, templateState.league_division_id, leaderboards]);

  async function loadTemplate(token?: CancelToken) {
    const res = await GetLeagueFormatTemplate({ id: templateId }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading format template")));
      return;
    }

    const template = res.data[0];

    const customPoints = JSON.parse(template.custom_points);

    const formattedCustomPoints =
      template.holes === "9_holes"
        ? [
            { id: "four_over", label: t("4 or More"), points: customPoints.four_over },
            { id: "three_over", label: "3", points: customPoints.three_over },
            { id: "two_over", label: "2", points: customPoints.two_over },
            { id: "one_over", label: "1", points: customPoints.one_over },
            { id: "even", label: t("Even"), points: customPoints.even },
            { id: "one_under", label: "-1", points: customPoints.one_under },
            { id: "two_under", label: "-2", points: customPoints.two_under },
            { id: "three_under", label: "-3", points: customPoints.three_under },
            { id: "four_under", label: "-4", points: customPoints.four_under },
            { id: "five_under", label: "-5", points: customPoints.five_under },
            { id: "six_under", label: "-6", points: customPoints.six_under },
            { id: "seven_under", label: t("-7 or Less"), points: customPoints.seven_under },
          ]
        : [
            { id: "eight_over", label: t("8 or More"), points: customPoints.eight_over },
            { id: "seven_over", label: "7", points: customPoints.seven_over },
            { id: "six_over", label: "6", points: customPoints.six_over },
            { id: "five_over", label: "5", points: customPoints.five_over },
            { id: "four_over", label: "4", points: customPoints.four_over },
            { id: "three_over", label: "3", points: customPoints.three_over },
            { id: "two_over", label: "2", points: customPoints.two_over },
            { id: "one_over", label: "1", points: customPoints.one_over },
            { id: "even", label: t("Even"), points: customPoints.even },
            { id: "one_under", label: "-1", points: customPoints.one_under },
            { id: "two_under", label: "-2", points: customPoints.two_under },
            { id: "three_under", label: "-3", points: customPoints.three_under },
            { id: "four_under", label: "-4", points: customPoints.four_under },
            { id: "five_under", label: "-5", points: customPoints.five_under },
            { id: "six_under", label: "-6", points: customPoints.six_under },
            { id: "seven_under", label: "-7", points: customPoints.seven_under },
            { id: "eight_under", label: "-8", points: customPoints.eight_under },
            { id: "nine_under", label: "-9", points: customPoints.nine_under },
            { id: "ten_under", label: t("-10 or Less"), points: customPoints.ten_under },
          ];

    const formattedLeaderboardData = JSON.parse(template.leaderboard_data);

    setTemplateState({
      ...res.data[0],
      format_template_id: template.id,
      handicap_allowance: template.handicap_allowance * 100,
      custom_points: formattedCustomPoints,
      leaderboard_data: formattedLeaderboardData,
    });
  }

  async function loadLeagueLeaderboards(leagueId: string | number, token?: CancelToken) {
    const res = await GetSeasonLeaderboard({ league_id: leagueId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league leaderboards")));
      return;
    }

    setLeaderboards(res.data);
  }

  async function loadLeagueDivision(leagueId: string | number, token?: CancelToken) {
    const res = await GetLeagueDivision({ league_id: leagueId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league division")));
      return;
    }

    if (res.data.length > 0) {
      setTemplateState(prev => ({ ...prev, league_division_id: res.data[0].id }));
    }

    setDivisions(res.data);
  }

  function handleChangeHoles(value: "9_holes" | "18_holes") {
    let formattedCustomPoints: any[] = [];

    if (templateState.custom_points) {
      formattedCustomPoints =
        value === "9_holes"
          ? templateState.holes === "18_holes"
            ? [...templateState.custom_points.splice(4, 12)]
            : [...templateState.custom_points]
          : templateState.holes === "9_holes"
          ? [
              { id: "eight_over", label: t("8 or More"), points: 0 },
              { id: "seven_over", label: "7", points: 0 },
              { id: "six_over", label: "6", points: 0 },
              { id: "five_over", label: "5", points: 0 },
              ...templateState.custom_points,
              { id: "eight_under", label: "-8", points: 0 },
              { id: "nine_under", label: "-9", points: 0 },
              { id: "ten_under", label: t("-10 or Less"), points: 0 },
            ]
          : [...templateState.custom_points];
    }

    setTemplateState(prevState => ({
      ...prevState,
      custom_points: formattedCustomPoints,
      holes: value,
    }));
  }

  function formatScoringPoints() {
    let formattedScoringPoints = {};

    for (let i = 0; i < templateState.custom_points.length; i++) {
      formattedScoringPoints = {
        ...formattedScoringPoints,
        [templateState.custom_points[i].id]: Number(templateState.custom_points[i].points),
      };
    }

    return formattedScoringPoints;
  }

  async function saveScoringFormatTemplate() {
    console.log("State", templateState);
    const params: TPutLeagueFormatTemplate = {
      ...templateState,
      // No gross score if organization is individual
      team_scores_best_gross:
        templateState.organization === "individual" ? undefined : templateState.team_scores_best_gross,
      // No net score if organization is individual OR team_scoring is ALL
      team_scores_best_net:
        templateState.organization === "individual" ? undefined : templateState.team_scores_best_net,
      // No score combination if team_scoring is BEST
      team_scores_combination:
        templateState.team_scores === "best_scores" ? undefined : templateState.team_scores_combination,
      handicap_allowance: templateState.handicap_allowance / 100,
      custom_points: formatScoringPoints(),
    };

    if (!params.league_division_id) {
      dispatch(showError(t("A league division is required to save a format template.")));
      return;
    }

    const res = await PutLeagueFormatTemplate(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error updating format template.")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated format template.")));
    history.push(`/admin/league/${leagueId}/league-settings/format-templates`);
  }

  async function deleteTemplate() {
    const deleteRes = await DeleteLeagueFormatTemplate({ format_template_id: templateId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(typeof deleteRes.data === "string" ? deleteRes.data : t("Error deleting format template.")));
      return;
    }

    dispatch(showSuccess(t("Successfully deleted format template.")));
    history.push(`/admin/league/${leagueId}/league-settings/format-templates`);
  }

  function handleChangeScoringPoints(id: string, value: string) {
    const tempScoringPoints = [...templateState.custom_points];

    tempScoringPoints.find((point: any) => point.id === id).points = value;

    setTemplateState(prevState => ({ ...prevState, custom_points: tempScoringPoints }));
  }

  function handleLeaderboardCheckboxChange(leaderboardId: number) {
    let tempLeaderboardData = templateState.leaderboard_data;

    if (tempLeaderboardData === null) {
      tempLeaderboardData = [];
    }

    const leaderboardIndex = tempLeaderboardData.findIndex(data => data.id === leaderboardId);

    if (leaderboardIndex < 0) {
      tempLeaderboardData.push({
        id: leaderboardId,
        point_allocation: templateState.format === "stroke_play" ? "posted" : "posted_points",
        custom_points: null,
      });
    } else {
      tempLeaderboardData.splice(leaderboardIndex, 1);
    }

    setTemplateState(prevState => ({ ...prevState, leaderboard_data: tempLeaderboardData }));
  }

  function handleAddCustomPoint() {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints.push({ position: Number(tempCustomPoints[tempCustomPoints.length - 1].position) + 1, points: 0 });

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleRemoveCustomPoint() {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints.splice(tempCustomPoints.length - 1, 1);

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleEditCustomPoint(value: number | string, pointIndex: number) {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints[pointIndex].points = Number(value);

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleSaveCustomPoints() {
    const tempLeaderboardData = cloneDeep(templateState.leaderboard_data);

    tempLeaderboardData[customPointsModal.selectedCustomPointsIndex].custom_points = customPointsModal.customPoints;

    setTemplateState(prevState => ({ ...prevState, leaderboard_data: tempLeaderboardData }));
    updateCustomPointsModal({ isOpen: false, selectedCustomPointsIndex: null, customPoints: null });
  }

  const primaryAction: IPrimaryPageAction = {
    action: () => void saveScoringFormatTemplate(),
    content: t("Save"),
  };

  const secondaryActions = [
    {
      action: () => updateDeletePopup({ isOpen: true }),
      content: t("Delete"),
    },
  ];

  return (
    <Page
      title={t("Edit Format Template")}
      subtitle={templateState.name}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Format Templates"),
          url: `/admin/league/${leagueId}/league-settings/format-templates`,
        },
      ]}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                type="text"
                label={t("Template Name")}
                id="name"
                value={templateState.name}
                onChange={e => handleChangeEventInput(e, templateState, setTemplateState)}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                type="text"
                label={t("Format Title")}
                id="title"
                value={templateState.title}
                onChange={e => handleChangeEventInput(e, templateState, setTemplateState)}
              />
            </FormLayout.Group>
            {divisions.length >= 2 && (
              <FormLayout.Group>
                <Select
                  label={t("League Division")}
                  defaultValue={templateState.league_division_id}
                  onChange={(value: number) => setTemplateState(prev => ({ ...prev, league_division_id: value }))}
                  disabled={divisions.length < 2}
                >
                  {divisions.map(division => (
                    <Select.Option key={division.id} value={division.id}>
                      {division.title}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>
            )}
          </FormLayout>
        </Card.Section>

        <Card.Section title={t("Individual or Team Scoring")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                defaultValue={templateState.organization}
                onChange={(value: TCompetitionOrganization) =>
                  setTemplateState(prev => ({
                    ...prev,
                    organization: value,
                    team_scores: value === "individual" ? undefined : prev.team_scores,
                    team_scores_combination: value === "individual" ? undefined : prev.team_scores_combination,
                    team_scores_best_net: value === "individual" ? 0 : prev.team_scores_best_net,
                    team_scores_best_gross: value === "individual" ? 0 : prev.team_scores_best_gross,
                    competition: undefined,
                  }))
                }
              >
                {organizationOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section title={t("Scoring")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("Scoring Format")}
                defaultValue={templateState.format}
                onChange={(value: TCompetitionFormat) => setTemplateState(prev => ({ ...prev, format: value }))}
              >
                {formatOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>

              <Select
                label={t("Competition")}
                defaultValue={templateState.competition}
                onChange={(value: TPlayerCompetition) => setTemplateState(prev => ({ ...prev, competition: value }))}
                disabled={templateState.organization === undefined}
              >
                {playerCompetitionOptions
                  .filter(val => val.organization === templateState.organization)
                  .map(option => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.label}
                    </Select.Option>
                  ))}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Holes")}
                defaultValue={templateState.holes}
                onChange={(value: TCompetitionFormatHoles) => handleChangeHoles(value)}
              >
                {formatHoleOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>

              <Input
                label={t("Default Participation Points")}
                id="default_participation_points"
                type="number"
                value={templateState.default_participation_points}
                onChange={e => handleChangeEventInput(e, templateState, setTemplateState)}
              />
            </FormLayout.Group>
            {templateState.format === "points_by_score" && (
              <div className="league-scoring-format-new-custom_points_container">
                {templateState.custom_points?.map((point: any) => {
                  return (
                    <div key={point.id} className="league-scoring-format-new-custom_points_input">
                      <Input
                        type="number"
                        label={point.label}
                        id={point.id}
                        value={point.points}
                        onChange={e => handleChangeScoringPoints(point.id, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </FormLayout>
        </Card.Section>

        {templateState.organization === "team" ? (
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t("Team Scoring")}
                  defaultValue={templateState.team_scores}
                  onChange={(value: TCompetitionTeamScores) =>
                    setTemplateState(prev => ({
                      ...prev,
                      team_scores: value,
                    }))
                  }
                >
                  {teamScoreOptions.map(option => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>

                {(templateState.team_scores === "best_scores" || templateState.team_scores === "gross_scores") && (
                  <Input
                    key={0}
                    label={t("Gross Scores")}
                    id="team_scores_best_gross"
                    type="number"
                    value={templateState.team_scores_best_gross}
                    onChange={e => handleChangeEventInput(e, templateState, setTemplateState)}
                  />
                )}

                {(templateState.team_scores === "best_scores" || templateState.team_scores === "net_scores") && (
                  <Input
                    key={0}
                    label={t("Net Scores")}
                    id="team_scores_best_net"
                    type="number"
                    value={templateState.team_scores_best_net}
                    onChange={e => handleChangeEventInput(e, templateState, setTemplateState)}
                  />
                )}
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        ) : null}

        <Card.Section title={t("Handicaps")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                defaultValue={templateState.handicap}
                label={t("Handicap")}
                onChange={(value: TCompetitionHandicap) => setTemplateState(prev => ({ ...prev, handicap: value }))}
              >
                {handicapOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        {filteredLeaderboards?.length > 0 && templateState.organization && templateState.format !== "skins" ? (
          <Card.Section title={t("Leaderboards")}>
            <div className={"ui-checkbox-group formats-checkbox-group"}>
              {filteredLeaderboards?.map((leaderboard, index) => (
                <div key={index}>
                  <div
                    onClick={() => handleLeaderboardCheckboxChange(leaderboard.id)}
                    className={classNames("ui-checkbox-group-item", {
                      "ui-checkbox-group-item_selected":
                        templateState.leaderboard_data?.findIndex(data => data.id === leaderboard.id) >= 0,
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <p className="location-title">{leaderboard.title}</p>
                    </div>
                    <div className="pointer-events-none">
                      <Checkbox
                        key={index}
                        checked={templateState.leaderboard_data?.findIndex(data => data.id === leaderboard.id) >= 0}
                        fullWidth
                        size="medium"
                        onChange={() => handleLeaderboardCheckboxChange(leaderboard.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Section>
        ) : (
          <Card.Section>
            <Callout
              type="info"
              title={t("No Leaderboards Available")}
              content={t("No leaderboards have been created for the selected division and organization")}
              actions={{
                primary: {
                  label: t("Leaderboards"),
                  onClick: () => history.push(`/admin/league/${leagueId}/scoring/leaderboards`),
                },
              }}
            />
          </Card.Section>
        )}
      </Card>

      <Portal isMounted={customPointsModal.isOpen}>
        <Sheet
          open={customPointsModal.isOpen}
          title={t("Custom Leaderboard Points")}
          okText={t("Save")}
          onCancel={closeCustomPointsModal}
          closable
          onOk={handleSaveCustomPoints}
          size="small"
        >
          <DataTable
            columns={[
              { label: t("Position") },
              { label: t("Points") },
            ]}
          >
            {customPointsModal.customPoints?.map((point: any, index: number) => (
              <tr key={point.position}>
                <td>{point.position}</td>
                <td>
                  <Input
                    labelHidden
                    id={"handicap_allowance"}
                    type="number"
                    value={point.points}
                    onChange={e => handleEditCustomPoint(e.target.value, index)}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ alignItems: "center" }}>
                <Button onClick={handleAddCustomPoint} type="tertiary" size="small">
                  <FontAwesomeIcon icon={["far", "plus"]} size={"2x"} />
                </Button>
              </td>
              <td style={{ alignItems: "center" }}>
                <Button
                  onClick={handleRemoveCustomPoint}
                  type="tertiary"
                  size="small"
                  disabled={customPointsModal.customPoints?.length <= 1}
                >
                  <FontAwesomeIcon icon={["far", "minus"]} size={"2x"} />
                </Button>
              </td>
            </tr>
          </DataTable>
        </Sheet>
      </Portal>

      <Portal isMounted={deletePopup.isOpen}>
        <Popup
          open={deletePopup.isOpen}
          title={t("Delete Format Template")}
          description={t("Are you sure you want to delete this format template? This cannot be undone.")}
          onOk={deleteTemplate}
          okText={t("Delete")}
          onCancel={closeDeletePopup}
          type="warning"
        />
      </Portal>
    </Page>
  );
}
